import type { BillingCycleWorksheetFullUpdate } from '../../../services/api/types.gen';
import type { WorksheetApiRead, WorksheetEditorState } from './types';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

// The backend hard-rejects '' for date fields; normalize to null defensively.
// No known path produces '' (pickers emit null, stored blobs persist via
// model_dump as strict ISO), so this is belt-and-braces only.
const emptyDateToNull = <T extends string | null | undefined>(
  value: T
): T | null => (value === '' ? null : value);

export function worksheetToEditorState(
  worksheet: WorksheetApiRead
): WorksheetEditorState | null {
  return worksheet.ub04_claim_data
    ? { ub04: clone(worksheet.ub04_claim_data) }
    : null;
}

export function editorStateToFullUpdate(
  state: WorksheetEditorState
): BillingCycleWorksheetFullUpdate {
  const ub04 = clone(state.ub04);
  ub04.condition_codes = (ub04.condition_codes ?? []).filter(
    code => code.trim() !== ''
  );
  ub04.occurrence_uses = (ub04.occurrence_uses ?? []).filter(
    use => use.code.trim() !== ''
  );
  ub04.value_codes = (ub04.value_codes ?? []).filter(
    value => value.code.trim() !== '' || value.amount.trim() !== ''
  );
  ub04.payer_rows?.forEach(payer => {
    if (payer.prior_payment?.trim() === '') {
      payer.prior_payment = null;
    }
    if (payer.estimated_amount_due?.trim() === '') {
      payer.estimated_amount_due = null;
    }
  });
  ub04.statement_from = emptyDateToNull(ub04.statement_from);
  ub04.statement_through = emptyDateToNull(ub04.statement_through);
  ub04.admission_date = emptyDateToNull(ub04.admission_date);
  if (ub04.patient) {
    ub04.patient.date_of_birth = emptyDateToNull(ub04.patient.date_of_birth);
  }
  if (ub04.condition_context) {
    const context = ub04.condition_context;
    context.accident_date = emptyDateToNull(context.accident_date);
    context.date_of_current_illness_or_injury = emptyDateToNull(
      context.date_of_current_illness_or_injury
    );
    context.hospitalization_from_date = emptyDateToNull(
      context.hospitalization_from_date
    );
    context.hospitalization_to_date = emptyDateToNull(
      context.hospitalization_to_date
    );
  }
  ub04.procedures?.forEach(procedure => {
    procedure.date = emptyDateToNull(procedure.date);
  });
  ub04.service_lines?.forEach(line => {
    line.service_date = emptyDateToNull(line.service_date);
  });
  return { ub04_claim_data: ub04 } as BillingCycleWorksheetFullUpdate;
}

export function isTerminalWorksheetStatus(status?: string | null): boolean {
  return status === 'PAID' || status === 'CANCELLED';
}

/**
 * Statuses where the cycle's worksheet opens read-only: the payer
 * adjudicated the claim (PAID, CANCELLED) or refused it (DENIED, EMR-1253 —
 * the claim is appealed or replaced, not corrected and resent), so editing
 * the worksheet would only restate a billed row.
 */
export const terminalWorksheetViewStatuses = new Set([
  'PAID',
  'CANCELLED',
  'DENIED',
]);

let rowIdCounter = 0;

// Every call returns a new id: the server turns each row id into the line's
// control number on the claim, and two rows must never share one.
export const newRowId = (): string => {
  const random = globalThis.crypto?.randomUUID?.();
  if (random) return random;
  rowIdCounter += 1;
  const suffix =
    `${Date.now().toString(16)}${rowIdCounter.toString(16).padStart(8, '0')}`
      .padStart(12, '0')
      .slice(-12);
  return `00000000-0000-4000-8000-${suffix}`;
};
